import icEnterFullScreen from "@/assest/ic_fullscreen_enter.png"
import icExitFullScreen from "@/assest/ic_fullscreen_exit.png"
import { useAppDispatch, useAppSelector } from "@/store"
import {
  selectIsFullScreen,
  updateFullScreen,
} from "@/stores/remote_desk_state_slice"
import { ConnectionUseCase } from "@/usecases/connection"
import { AppIconTextButton } from "@/widgets/Common"
import React, { RefObject, useCallback, useEffect } from "react"

export async function exitDocumentFullScreen(
  onExited?: () => void
): Promise<void> {
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen()
    }
  } catch (err) {
    console.error("Error attempting to exit full-screen mode:", err)
  } finally {
    onExited?.()
  }
}

export const FullScreenButton: React.FC = () => {
  const isFullScreen = useAppSelector(selectIsFullScreen)
  const dispatch = useAppDispatch()
  const toggleFullScreen = () => {
    // 解决ipad的chrome浏览器 全屏时报错（需要用户交互）
    if (!isFullScreen) {
      document.documentElement
        .requestFullscreen()
        .then(() => {
          dispatch(updateFullScreen(true))
        })
        .catch((err) => {
          console.error("Error attempting to enable full-screen mode:", err)
        })
    } else {
      void exitDocumentFullScreen(() => {
        if (navigator.keyboard) {
          navigator.keyboard.unlock()
        }
        dispatch(updateFullScreen(false))
      })
    }
  }

  let icon: string
  if (isFullScreen) {
    // 全屏模式
    icon = icExitFullScreen
  } else {
    // 非全屏模式
    icon = icEnterFullScreen
  }
  return (
    <AppIconTextButton
      src={icon}
      size="20px"
      padding="3px"
      fontSize="11px"
      textString={"全屏"}
      onClick={() => {
        toggleFullScreen()
      }}
    />
  )
}

export function useFullScreen(viewRef: RefObject<HTMLElement>) {
  const isFullScreen = useAppSelector(selectIsFullScreen)
  const dispatch = useAppDispatch()

  // 判断当前是否有全屏组件
  const hasFullScreenElement = useCallback(() => {
    const fullScreenElement = document.fullscreenElement
    return fullScreenElement !== null && fullScreenElement !== undefined
  }, [])

  const enterFullScreen = useCallback(() => {
    if (hasFullScreenElement()) {
      return
    }

    viewRef.current?.requestFullscreen()
  }, [hasFullScreenElement, viewRef])

  const exitFullScreen = useCallback(() => {
    if (!hasFullScreenElement()) {
      return
    }

    void exitDocumentFullScreen()
  }, [hasFullScreenElement])

  // 初始化操作
  useEffect(() => {
    const handleFullScreenChange = () => {
      const isInFullScreen = hasFullScreenElement()
      dispatch(updateFullScreen(isInFullScreen))
    }
    // 监听全屏状态变化
    document.addEventListener("fullscreenchange", handleFullScreenChange)
    const unlockKeyboard = () => {
      if (navigator.keyboard) {
        navigator.keyboard.unlock()
      }
    }
    // 处理页面刷新
    window.addEventListener("beforeunload", unlockKeyboard)
    const dispose = () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange)
      window.removeEventListener("beforeunload", unlockKeyboard)
      unlockKeyboard()
    }
    return dispose
  }, [dispatch, hasFullScreenElement])

  // 处理全屏状态改变
  useEffect(() => {
    if (isFullScreen) {
      enterFullScreen()
      // 拦截键盘事件
      if (navigator.keyboard) {
        navigator.keyboard.lock()
      }
    } else {
      exitFullScreen()
      // 取消拦截键盘事件
      if (navigator.keyboard) {
        navigator.keyboard.unlock()
      }
    }
  }, [enterFullScreen, exitFullScreen, isFullScreen])

  useEffect(() => {
    // 退出远程桌面时关闭全屏
    ConnectionUseCase.addConnCallback({
      onDisconnect: () => {
        void exitDocumentFullScreen(() => {
          if (navigator.keyboard) {
            navigator.keyboard.unlock()
          }
          dispatch(updateFullScreen(false))
        })
      },
    })
  }, [dispatch])
}

declare global {
  interface Navigator {
    keyboard?: any
  }
}
